import React, { useId } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import type { SxProps, Theme, SelectProps } from '@mui/material';

export interface FacilitySelectorOption {
  id: number | string;
  name: string;
}

export interface FacilitySelectorProps {
  facilities: FacilitySelectorOption[];
  value: number | string;
  onChange: (event: SelectChangeEvent<any>) => void;
  label?: string;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'standard' | 'filled';
  fullWidth?: boolean;
  disabled?: boolean;
  /** Render "(id) name" (instead of just "name"), wrapped to prevent overflow. */
  showId?: boolean;
  /** Prepend a synthetic option (e.g. an "All Facilities" choice) above the facility list. */
  allOption?: { value: string; label: string };
  sx?: SxProps<Theme>;
  /** Merged into the Select display element; `aria-label` defaults to `label`. */
  selectDisplayProps?: SelectProps['SelectDisplayProps'];
}

/**
 * Shared facility-picker dropdown used across pages/modals that let a user
 * scope their view to a single facility (or, via `allOption`, all facilities).
 */
const FacilitySelector: React.FC<FacilitySelectorProps> = ({
  facilities,
  value,
  onChange,
  label = 'Facility',
  size = 'small',
  variant = 'outlined',
  fullWidth = false,
  disabled = false,
  showId = false,
  allOption,
  sx = { minWidth: 200 },
  selectDisplayProps,
}) => {
  const labelId = `${useId()}-label`;

  return (
    <FormControl variant={variant} size={size} fullWidth={fullWidth} disabled={disabled} sx={sx}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value}
        onChange={onChange}
        label={label}
        SelectDisplayProps={{ 'aria-label': label, ...selectDisplayProps }}
      >
        {allOption && <MenuItem value={allOption.value}>{allOption.label}</MenuItem>}
        {facilities.map(facility => (
          <MenuItem key={facility.id} value={facility.id}>
            {showId ? (
              <Typography variant="body2" noWrap>
                ({facility.id}) {facility.name}
              </Typography>
            ) : (
              facility.name
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FacilitySelector;
